import { useState, useEffect } from 'react';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import IconButton from '@material-ui/core/IconButton';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import Collapse from '@material-ui/core/Collapse';

import CardHeader from '@material-ui/core/CardHeader';
import Avatar from '@material-ui/core/Avatar';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import { actions } from '@store/app';
import useActions from '@shared/hooks/useActions';
import { UserRole } from '@shared/models';
import Typography from '../typography';
import { navRoutes } from '@shared/constants';

const drawerWidth = 256;

const styles = (theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    wordBreak: 'break-all',
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: theme.spacing(5) + 2,
    whiteSpace: 'nowrap',
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(7) + 1,
    },
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    justifyContent: 'flex-end',
    padding: '0 8px',
    overflowX: 'hidden',
    borderRight: 'none',
    ...theme.mixins.toolbar,
  },
  navList: {
    padding: theme.spacing(2.0),
    paddingBottom: theme.spacing(7.5),
    borderBottom: `.1rem solid ${theme.palette.common.lightBlue}`,
  },
  navLink: {
    position: 'relative',
    display: 'block',
    marginBottom: theme.spacing(2.5),

    '&:hover': {
      '& span': {
        color: theme.palette.primary.main,
      },
      '& path': {
        fill: theme.palette.primary.main,
      },
      '& rect:not(.ignore)': {
        fill: theme.palette.primary.main,
      },
      '& rect.ignore': {
        stroke: theme.palette.primary.main,
      },
      '& div': {
        display: 'block',
      },
    },

    '&.active': {
      '& span': {
        color: theme.palette.primary.main,
      },
      '& div': {
        '& path': {
          fill: theme.palette.primary.main,
        },
        '& rect:not(.ignore)': {
          fill: theme.palette.primary.main,
        },
        '& rect.ignore': {
          stroke: theme.palette.primary.main,
        },
      },
    },
    '&.disabled': {
      opacity: 0.4,
      pointerEvents: 'none',

      '&:hover': {
        '& span': {
          color: 'inherit !important',
        },
        '& path': {
          fill: 'inherit !important',
        },
        '& rect:not(.ignore)': {
          fill: 'inherit !important',
        },
        '& rect.ignore': {
          stroke: 'inherit !important',
        },
      },
    },
  },
  navTip: {
    display: 'none',
    width: 'max-content',
    position: 'fixed',
    left: theme.spacing(1.5),
    marginTop: theme.spacing(4.375),
    fontWeight: 'normal',
    fontSize: '1.2rem',
    lineHeight: '1rem',
    color: theme.palette.common.white,
    background: theme.palette.primary.main,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 1.375),
    cursor: 'default',
    overflow: 'visible',
    zIndex: theme.zIndex.tooltip,

    '&:before': {
      position: 'absolute',
      top: theme.spacing(-0.625),
      left: theme.spacing(2.5),
      borderLeft: '.5rem solid transparent',
      borderRight: '.5rem solid transparent',
      borderTop: `.5rem solid ${theme.palette.primary.main}`,
      content: '""',
      fontSize: 0,
      lineHeight: 0,
      marginLeft: theme.spacing(-0.625),
      width: theme.spacing(0),
      transform: 'rotate(180deg)',
    },
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
  },
  subNav: {
    paddingLeft: theme.spacing(2.25),
    marginTop: theme.spacing(1),
  },
  subNavItem: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  navImage: {
    display: 'block',
    marginRight: theme.spacing(1.2),
    width: theme.spacing(2.75),
    height: theme.spacing(2.75),
    '&:last-child': {
      marginRight: 0,
      marginLeft: 'auto',
    },
  },
  navText: {
    fontWeight: '500',
    fontSize: '1.4rem',
    lineHeight: '2.1rem',
    letterSpacing: '0.01em',
    color: theme.palette.common.darkGrey,
  },
  navCopyright: {
    padding: '20px',
    marginTop: 'auto',
    fontWeight: '500',
    fontSize: '1.1rem',
    lineHeight: '1.6rem',
    letterSpacing: '0.01em',
    color: theme.palette.common.grey,
  },
  navBlock: {
    position: 'relative',
    padding: theme.spacing(2.5),
  },
  profileSettingsBtn: {
    transform: 'rotate(90deg)',
    padding: '.3rem !important',
    marginRight: '1.6rem !important',
    color: `${theme.palette.common.grey} !important`,
  },
  navSettingsLink: {
    padding: theme.spacing(2.5),
    paddingBottom: theme.spacing(0),
    position: 'relative',
    display: 'block',

    '& span': {
      marginLeft: theme.spacing(0.5),
    },

    '&:last-child': {
      marginBottom: 0,
    },

    '&:hover': {
      '& span': {
        color: theme.palette.primary.main,
      },
      '& path': {
        fill: theme.palette.primary.main,
      },
      '& rect:not(.ignore)': {
        fill: theme.palette.primary.main,
      },
      '& rect.ignore': {
        stroke: theme.palette.primary.main,
      },
      '& div': {
        display: 'block',
      },
    },

    '&.active': {
      '& span': {
        color: theme.palette.primary.main,
      },
      '& div': {
        '& path': {
          fill: theme.palette.primary.main,
        },
        '& rect:not(.ignore)': {
          fill: theme.palette.primary.main,
        },
        '& rect.ignore': {
          stroke: theme.palette.primary.main,
        },
      },
    },
  },
  navArrowDrop: {
    color: theme.palette.common.grey,
    fontSize: '2.4rem',
    height: theme.spacing(2.75),
    width: theme.spacing(2.75),
  },
  menuToggleButton: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    justifyContent: 'center',
    width: '20px',
    height: '56px',
    position: 'relative',
    zIndex: 9999,
    left: '-1px',
    border: `1px solid ${theme.palette.common.paleGrey}`,
    borderLeft: 'none',
    borderTopRightRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
      transition: 'fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    },
    '& svg': {
      fontSize: '2rem',
    },
  },
});


function AppMenu({ classes, onSelect }) {
  const { showMenu, logout } = useActions(actions);

  const [anchorEl, setAnchorEl] = useState();
  const [openSettings, setOpenSettings] = useState();
  const [openSubMenus, setOpenSubMenus] = useState({});

  const menuOpen = useSelector((state) => state.app.menuOpen);
  const user = useSelector((state) => state.app.user);
  const location = useLocation();

  const { toggleMenu } = useActions(actions);

  useEffect(() => {
    const newOpenSubMenus = {};

    const findParentRoutes = (routes, path) => {
      for (const route of routes) {
        if (route.subItems) {
          const found = route.subItems.some(item => item.path === path);
          if (found) {
            newOpenSubMenus[route.name] = true;
            return true;
          }
          if (findParentRoutes(route.subItems, path)) {
            newOpenSubMenus[route.name] = true;
            return true;
          }
        }
      }
      return false;
    };

    findParentRoutes(navRoutes, location.pathname);
    setOpenSubMenus(newOpenSubMenus);
  }, [location.pathname]);

  const defaultRoutes = navRoutes.filter((r) => isEmpty(r.roles));
  const rolesRoutes = navRoutes.filter((r) => !isEmpty(r.roles));

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleNavLinkClick = () => {
    showMenu();
    onSelect();
  };

  const handleOpenSettings = () => {
    setOpenSettings((prevOpenSettings) => !prevOpenSettings);
  };

  const toggleSubMenu = (menuName) => {
    setOpenSubMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName],
    }));
  };

  const createInitials = () => {
    if (user && user.firstname && user.lastname) {
      const initials = `${user.firstname.charAt(0)}${user.lastname.charAt(0)}`;
      return initials.toUpperCase();
    }
    return 'N/A';
  };

  const titleNameSurname = `${user.firstname} ${user.lastname}`;

  const renderMenuItem = (route) => (
    <NavLink
      className={ classNames(classes.navLink, {
        disabled: route.disabled,
      }) }
      key={ route.path }
      to={ route.path }
      onClick={ route.disabled ? (e) => e.preventDefault() : handleNavLinkClick }
    >
      {!menuOpen && (
        <div className={ classes.navTip }>{ route.name }</div>
      )}
      <li className={ classes.navItem }>
        <div className={ classes.navImage }>{ route.icon }</div>
        {menuOpen && (
          <Typography
            className={ classes.navText }
            component="span"
          >
            { route.name }
          </Typography>
        )}
      </li>
    </NavLink>
  );

  const renderSubMenu = (route) => {
    const isOpen = openSubMenus[route.name];

    return (
      <div key={ route.name }>
        <div
          className={ classNames(classes.navLink, {
            disabled: route.disabled,
          }) }
          onClick={ () => toggleSubMenu(route.name) }
          style={ { cursor: 'pointer' } }
        >
          {!menuOpen && (
            <div className={ classes.navTip }>{ route.name }</div>
          )}
          <li className={ classes.navItem }>
            <div className={ classes.navImage }>{ route.icon }</div>
            {menuOpen && (
              <>
                <Typography
                  className={ classes.navText }
                  component="span"
                >
                  { route.name }
                </Typography>
                <div className={ classes.navImage }>
                  {isOpen ? <ArrowDropUpIcon className={ classes.navArrowDrop } />
                    : <ArrowDropDownIcon className={ classes.navArrowDrop } />}
                </div>
              </>
            )}
          </li>
        </div>

        <Collapse in={ isOpen && menuOpen } timeout="auto" unmountOnExit>
          <div className={ classes.subNav }>
            {route.subItems.map(subItem => (
              <div key={ subItem.path } className={ classes.subNavItem }>
                {renderMenuItem(subItem)}
              </div>
            ))}
          </div>
        </Collapse>
      </div>
    );
  };

  return (
    <div style={ { position: 'relative', display: 'flex', alignItems: 'center' } }>
    <Drawer
      variant="permanent"
      style={ { overflowY: 'hidden', boxShadow: '6px 0px 18px rgb(0 0 0 / 6%)', borderRight: 'none' } }
      className={ classNames(classes.drawer, {
        [classes.drawerOpen]: menuOpen,
        [classes.drawerClose]: !menuOpen,
      }) }
      classes={ {
        paper: classNames({
          [classes.drawerOpen]: menuOpen,
          [classes.drawerClose]: !menuOpen,
        }),
      } }
      open={ menuOpen }
    >
      <div>
        <div className={ classes.toolbar } />
        <div>
          <CardHeader
            className={ menuOpen ? 'profile' : 'profile profile_shifted' }
            avatar={ (
              <Avatar aria-label="recipe" className="profile__icon_source">
                {createInitials()}
              </Avatar>
            ) }
            action={ (
              <IconButton
                aria-label="settings"
                aria-owns={ open ? 'menu-appbar' : undefined }
                onClick={ handleMenu }
                className="profile__settings"
              >
                <MoreVertIcon />
              </IconButton>
            ) }
            title={ titleNameSurname }
            subheader={ user.email }
          />
          <Menu
            id="menu-appbar"
            anchorEl={ anchorEl }
            anchorOrigin={ {
              vertical: 'top',
              horizontal: 'right',
            } }
            transformOrigin={ {
              vertical: 'top',
              horizontal: 'right',
            } }
            open={ open }
            onClose={ handleClose }
          >
            <MenuItem onClick={ logout }>Exit/Log Out</MenuItem>
          </Menu>
        </div>

        <ul className={ classes.navList }>
          {defaultRoutes.map(route => route.subItems ? renderSubMenu(route) : renderMenuItem(route))}
        </ul>

        <ul className={ classes.navSettingsLink } onClick={ handleOpenSettings }>
          <li className={ classes.navItem }>
            <div className={ classes.navImage }>
              {openSettings ? <ArrowDropUpIcon className={ classes.navArrowDrop } />
                : <ArrowDropDownIcon className={ classes.navArrowDrop } />}
            </div>
            <Typography
              className={ classes.navText }
              component="span"
            >
              Settings
            </Typography>
          </li>
        </ul>

        <Collapse in={ openSettings } timeout="auto" unmountOnExit>
          <ul className={ classes.navList }>
            {rolesRoutes.map(route => route.roles.includes(user.role) && (route.subItems ? renderSubMenu(route) : renderMenuItem(route)))}
          </ul>
        </Collapse>
      </div>

      {menuOpen && (
      <div className={ classes.navCopyright }>
        {`© ${new Date().getFullYear()} ${process.env.REACT_APP_BRAND ?? 'Zolak'}`}
      </div>
      )}
    </Drawer>
      <div
        className={ classes.menuToggleButton }
        onClick={ toggleMenu }
        size="small"
      >
        {menuOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
      </div>
    </div>
  );
}

export default (withStyles(styles, { withTheme: true })(AppMenu));
